import React, { useMemo } from 'react';
import { DataTable, Column } from '@/components/ui/data-table';
import { AudiencePill, AtsSourceIcon, StatusBadge, getStatusAccentColor } from '@/components/ui/table-components';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { Candidate } from '../../interfaces';

const statusLabels: Record<string, string> = {
  PLATFORM_INCLUDED: 'Pending Opt-in',
  SHARED: 'Shared',
  STOPPED_SHARING: 'Draft',
  REMOVED: 'Removed',
  UNKNOWN: 'Unknown',
};

interface CandidatesTableProps {
  candidates: Candidate[];
  locationName: (location: string) => string[];
  onCandidateClick: (candidate: Candidate) => void;
  onEditCandidate: (candidate: Candidate) => void;
  onDeleteCandidate: (candidateId: number) => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnId: string) => void;
  isLoading?: boolean;
}

export function CandidatesTable({
  candidates,
  locationName,
  onCandidateClick,
  onEditCandidate,
  onDeleteCandidate,
  sortBy,
  sortDirection,
  onSort,
  isLoading = false,
}: CandidatesTableProps) {
  const columns: Column<Candidate>[] = useMemo(
    () => [
      {
        id: 'firstName',
        header: 'Name',
        sortable: true,
        cell: (candidate) => (
          <div>
            <div className="font-medium text-slate-900 dark:text-slate-100">
              {candidate.firstName} {candidate.lastName?.slice(0, 1)}.
            </div>
            {candidate.externalId && (
              <div className="mt-1">
                <AtsSourceIcon externalId={candidate.externalId} />
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'idealJobTitles',
        header: 'Ideal Job Titles',
        sortable: true,
        cell: (candidate) => (
          <span className="text-slate-700 dark:text-slate-300">
            {candidate.idealJobTitles || '-'}
          </span>
        ),
      },
      {
        id: 'industry',
        header: 'Industry',
        sortable: true,
        cell: (candidate) => (
          <span className="text-slate-700 dark:text-slate-300">
            {candidate.industry || '-'}
          </span>
        ),
      },
      {
        id: 'location.displayName',
        header: 'Location',
        sortable: true,
        cell: (candidate) => (
          <span className="text-slate-700 dark:text-slate-300">
            {locationName(candidate?.location?.displayName as string)}
          </span>
        ),
      },
      {
        id: 'yearsOfExperience',
        header: 'Experience',
        sortable: true,
        cell: (candidate) => (
          <span className="text-slate-700 dark:text-slate-300">
            {candidate.yearsOfExperience ? `${candidate.yearsOfExperience} yrs` : '-'}
          </span>
        ),
      },
      {
        id: 'workType',
        header: 'Type',
        sortable: true,
        className: 'text-center',
        cell: (candidate) => (
          <Badge variant="secondary" className="font-mono text-xs">
            {candidate.workType
              ? candidate.workType === 'TEMP'
                ? 'CONTRACT/TEMP'
                : candidate.workType
              : '-'}
          </Badge>
        ),
      },
      {
        id: 'audience',
        header: 'Audience',
        sortable: true,
        className: 'text-center',
        cell: (candidate) => <AudiencePill audience={candidate.audience || 'PRIVATE'} />,
      },
      {
        id: 'matches',
        header: 'Matches',
        sortable: true,
        className: 'text-center',
        cell: (candidate) => (
          <Badge
            variant={candidate.matches ? 'default' : 'secondary'}
            className="font-serif text-base font-semibold"
          >
            {candidate.matches || 0}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        className: 'text-center',
        cell: (candidate) => (
          <StatusBadge status={statusLabels[candidate.status] || 'Unknown'} />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        width: '120px',
        className: 'text-center',
        cell: (candidate) => (
          <div className="flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onEditCandidate(candidate);
              }}
              className="hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/30 dark:hover:text-teal-300"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteCandidate(candidate.id);
              }}
              className="hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-900/30 dark:hover:text-rose-300"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [locationName, onEditCandidate, onDeleteCandidate]
  );

  const getRowId = (candidate: Candidate, index: number) => {
    if (candidate?.id != null && candidate.id !== 0) {
      return `candidate-${candidate.id}`;
    }
    if (candidate?.externalId) {
      return `candidate-ext-${candidate.externalId}`;
    }
    return `candidate-idx-${index}`;
  };

  return (
    <DataTable
      data={candidates}
      columns={columns}
      onRowClick={onCandidateClick}
      getRowId={getRowId}
      getRowAccentColor={(candidate) => getStatusAccentColor(candidate.status)}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onSort}
      isLoading={isLoading}
      emptyMessage="You have no candidates included at this time. Keep an eye on your inbox as we will send you an email when we detect potential roles for your candidates."
    />
  );
}
